import React, { FormEvent, useMemo, useState } from "react";

import AuthLayout from "../components/templates/AuthLayout";
import AuthService from "../services/AuthService";

type LoginResponse = {
  data: { token: string } | null;
  status: "success" | "error";
  code: number;
  errors: string[];
};

export default function DatabaseLogin() {
  const { login } = AuthService();
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [credentials, setCredentials] = useState({
    username: "",
    password: "",
  });

  const updateField = <K extends keyof typeof credentials>(
    field: K,
    value: (typeof credentials)[K],
  ) => {
    setCredentials((prev) => ({ ...prev, [field]: value }));
  };

  const { key, id } = useMemo(() => {
    const search = typeof window !== "undefined" ? window.location.search : "";
    const params = new URLSearchParams(search);
    return { key: params.get("k"), id: params.get("id") };
  }, []);

  const showSignInMessage = key === "signin";
  const showAccessMessage = key === "assistant_opt" || key === "pdfs";

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError("");
    setIsSubmitting(true);

    try {
      const response = (await login({
        username: credentials.username,
        password: credentials.password,
      })) as LoginResponse;

      if (response?.code !== 200) {
        setError(
          response?.errors?.[0] || "Username or password was incorrect.",
        );
        return;
      }

      if ((key === "assistant_opt" || key === "pdfs") && id) {
        const encodedKey = encodeURIComponent(key);
        const encodedId = encodeURIComponent(id);
        window.location.href = `/database/legal.php?k=${encodedKey}&id=${encodedId}`;
        return;
      }

      window.location.href = "/database/showUser.php";
    } catch (err) {
      setError("Username or password was incorrect.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AuthLayout>
      <h1 className="mb-1 text-2xl font-medium text-gray-900 dark:text-gray-100">
        Sign in
      </h1>
      <p className="mb-7 text-sm text-gray-500 dark:text-gray-400">
        Enter your credentials to access your account.
      </p>

      {showAccessMessage && (
        <div
          role="alert"
          className="mb-4 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-800 dark:border-blue-900 dark:bg-blue-950 dark:text-blue-200"
        >
          <strong>Sign in</strong> or <strong>register</strong> in order to
          access this section.
        </div>
      )}

      {showSignInMessage && (
        <div
          role="alert"
          className="mb-4 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-800 dark:border-blue-900 dark:bg-blue-950 dark:text-blue-200"
        >
          <strong>Congratulations!</strong> You can now sign in.
        </div>
      )}

      {error && (
        <div
          role="alert"
          className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-300"
        >
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="mb-1.5 flex items-baseline justify-between">
          <label
            htmlFor="username"
            className="mb-1.5 block text-sm font-medium text-gray-900 dark:text-gray-200"
          >
            Username
          </label>
        </div>
        <input
          id="username"
          type="text"
          autoComplete="username"
          required
          value={credentials.username}
          onChange={(e) => updateField("username", e.target.value)}
          placeholder="name@example.com"
          className="mb-4 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-slate-500 dark:bg-slate-700 dark:text-gray-100 dark:placeholder-gray-500"
        />

        <div className="mb-1.5 flex items-baseline justify-between">
          <label
            htmlFor="password"
            className="text-sm font-medium text-gray-900 dark:text-gray-200"
          >
            Password
          </label>
          <a
            href="/database/forgot.php"
            className="text-xs text-blue-700 hover:underline dark:text-blue-400"
          >
            Forgot password?
          </a>
        </div>
        <div className="relative mb-4">
          <input
            id="password"
            type={showPassword ? "text" : "password"}
            autoComplete="current-password"
            required
            value={credentials.password}
            onChange={(e) => updateField("password", e.target.value)}
            placeholder="••••••••"
            className="block w-full rounded-md border border-gray-300 px-3 py-2 pr-16 text-sm shadow-sm placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-slate-500 dark:bg-slate-700 dark:text-gray-100 dark:placeholder-gray-500"
          />
          <button
            type="button"
            onClick={() => setShowPassword((v) => !v)}
            aria-label={showPassword ? "Hide password" : "Show password"}
            aria-pressed={showPassword}
            className="absolute right-1 top-1/2 -translate-y-1/2 rounded px-2.5 py-1 text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            {showPassword ? "Hide" : "Show"}
          </button>
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="mb-4 flex w-full items-center justify-center rounded-md bg-blue-700 px-4 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-blue-400 dark:bg-blue-600 dark:hover:bg-blue-500 dark:focus:ring-offset-slate-800 dark:disabled:bg-blue-900 dark:disabled:text-blue-300"
        >
          {isSubmitting ? (
            <>
              <svg
                className="mr-2 h-4 w-4 animate-spin"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="3"
                  opacity="0.25"
                />
                <path
                  d="M4 12a8 8 0 0 1 8-8"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                />
              </svg>
              Signing in...
            </>
          ) : (
            "Sign in"
          )}
        </button>

        <p className="text-center text-sm text-gray-600 dark:text-gray-400">
          Don&apos;t have an account?{" "}
          <a
            href="/database/signup.php"
            className="font-medium text-blue-700 hover:underline dark:text-blue-400"
          >
            Sign up
          </a>
        </p>
      </form>
    </AuthLayout>
  );
}
